import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { get, post, patch, del } from "../client";
import type {
  Supplier,
  Budget,
  Order,
  CreateSupplierInput,
  UpdateSupplierInput,
  CreateBudgetInput,
  UpdateBudgetInput,
  CreateOrderInput,
  UpdateOrderInput,
  OrderActionResult,
  ReceiveAllResult,
} from "../types";

const SUPPLIERS_KEY = "suppliers";
const BUDGETS_KEY = "budgets";
const ORDERS_KEY = "orders";

// ===== SUPPLIERS =====

/**
 * Search suppliers.
 */
export function useSuppliersSearch(query?: string, status?: string, type?: string) {
  const params = new URLSearchParams();
  if (query) params.set("q", query);
  if (status) params.set("status", status);
  if (type) params.set("type", type);
  const queryString = params.toString();

  return useQuery({
    queryKey: [SUPPLIERS_KEY, "search", query, status, type],
    queryFn: async () => {
      const url = queryString ? `/acquisition/suppliers?${queryString}` : "/acquisition/suppliers";
      return get<Supplier[]>(url);
    },
  });
}

/**
 * Get a single supplier by ID.
 */
export function useSupplier(id: string | undefined) {
  return useQuery({
    queryKey: [SUPPLIERS_KEY, id],
    queryFn: async () => {
      if (!id) throw new Error("ID is required");
      return get<Supplier>(`/acquisition/suppliers/${id}`);
    },
    enabled: !!id,
  });
}

/**
 * Create a new supplier.
 */
export function useCreateSupplier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateSupplierInput) => {
      return post<Supplier>("/acquisition/suppliers", input);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [SUPPLIERS_KEY] });
    },
  });
}

/**
 * Update a supplier.
 */
export function useUpdateSupplier(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateSupplierInput) => {
      return patch<Supplier>(`/acquisition/suppliers/${id}`, input);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [SUPPLIERS_KEY] });
    },
  });
}

/**
 * Delete a supplier.
 */
export function useDeleteSupplier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await del(`/acquisition/suppliers/${id}`);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [SUPPLIERS_KEY] });
    },
  });
}

// ===== BUDGETS =====

/**
 * Get budgets by fiscal year.
 */
export function useBudgets(fiscalYear?: number, status?: string) {
  const params = new URLSearchParams();
  if (fiscalYear) params.set("fiscal_year", String(fiscalYear));
  if (status) params.set("status", status);
  const queryString = params.toString();

  return useQuery({
    queryKey: [BUDGETS_KEY, fiscalYear, status],
    queryFn: async () => {
      const url = queryString ? `/acquisition/budgets?${queryString}` : "/acquisition/budgets";
      return get<Budget[]>(url);
    },
  });
}

/**
 * Get a single budget by ID.
 */
export function useBudget(id: string | undefined) {
  return useQuery({
    queryKey: [BUDGETS_KEY, id],
    queryFn: async () => {
      if (!id) throw new Error("ID is required");
      return get<Budget>(`/acquisition/budgets/${id}`);
    },
    enabled: !!id,
  });
}

/**
 * Create a new budget.
 */
export function useCreateBudget() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateBudgetInput) => {
      return post<Budget>("/acquisition/budgets", input);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [BUDGETS_KEY] });
    },
  });
}

/**
 * Update a budget.
 */
export function useUpdateBudget(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateBudgetInput) => {
      return patch<Budget>(`/acquisition/budgets/${id}`, input);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [BUDGETS_KEY] });
    },
  });
}

/**
 * Delete a budget.
 */
export function useDeleteBudget() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await del(`/acquisition/budgets/${id}`);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [BUDGETS_KEY] });
    },
  });
}

// ===== ORDERS =====

/**
 * Search orders.
 */
export function useOrdersSearch(status?: string, supplierId?: string) {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  if (supplierId) params.set("supplier_id", supplierId);
  const queryString = params.toString();

  return useQuery({
    queryKey: [ORDERS_KEY, "search", status, supplierId],
    queryFn: async () => {
      const url = queryString ? `/acquisition/orders?${queryString}` : "/acquisition/orders";
      return get<Order[]>(url);
    },
  });
}

/**
 * Get a single order by ID.
 */
export function useOrder(id: string | undefined) {
  return useQuery({
    queryKey: [ORDERS_KEY, id],
    queryFn: async () => {
      if (!id) throw new Error("ID is required");
      return get<Order>(`/acquisition/orders/${id}`);
    },
    enabled: !!id,
  });
}

/**
 * Create a new order.
 */
export function useCreateOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateOrderInput) => {
      return post<Order>("/acquisition/orders", input);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [ORDERS_KEY] });
    },
  });
}

/**
 * Update an order.
 */
export function useUpdateOrder(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateOrderInput) => {
      return patch<Order>(`/acquisition/orders/${id}`, input);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [ORDERS_KEY] });
    },
  });
}

/**
 * Delete an order.
 */
export function useDeleteOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await del(`/acquisition/orders/${id}`);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [ORDERS_KEY] });
    },
  });
}

// ===== ORDER ACTIONS =====

/**
 * Submit an order for approval.
 */
export function useSubmitOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      return post<OrderActionResult>(`/acquisition/orders/${id}/submit`, {});
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [ORDERS_KEY] });
    },
  });
}

/**
 * Approve an order.
 */
export function useApproveOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      return post<OrderActionResult>(`/acquisition/orders/${id}/approve`, {});
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [ORDERS_KEY] });
    },
  });
}

/**
 * Send an order to the supplier.
 */
export function useSendOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      return post<OrderActionResult>(`/acquisition/orders/${id}/send`, {});
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [ORDERS_KEY] });
    },
  });
}

/**
 * Cancel an order.
 */
export function useCancelOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      return post<OrderActionResult>(`/acquisition/orders/${id}/cancel`, {});
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [ORDERS_KEY] });
    },
  });
}

/**
 * Close an order.
 */
export function useCloseOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      return post<OrderActionResult>(`/acquisition/orders/${id}/close`, {});
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [ORDERS_KEY] });
    },
  });
}

/**
 * Receive all items from an order.
 */
export function useReceiveAllItems() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      orderId,
      deliveryNoteNumber,
    }: {
      orderId: string;
      deliveryNoteNumber?: string;
    }) => {
      return post<ReceiveAllResult>(`/acquisition/orders/${orderId}/receive-all`, {
        deliveryNoteNumber,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [ORDERS_KEY] });
    },
  });
}

/**
 * Get order statistics.
 */
export function useOrderStatistics() {
  return useQuery({
    queryKey: [ORDERS_KEY, "statistics"],
    queryFn: async () => {
      return get<{
        statistics: Record<string, number>;
        pending_approval: number;
        awaiting_delivery: number;
      }>("/acquisition/orders/statistics");
    },
  });
}
